import { parseISO } from 'date-fns';
import {
  AvailableStore,
  CONSOLIDATED_STORE_ID,
  NO_STORE_ID,
  Slots,
} from './services/Appointment/model';

export type MS = number;

export type ServiceLocation =
  | 'inStore'
  | 'home'
  | 'storeRemote'
  | 'remote'
  | 'storeHome';

export const getLocationType = (
  locationType: ServiceLocation,
): ServiceLocation => {
  if (['remote', 'storeRemote'].includes(locationType)) {
    return 'remote';
  }
  if (['home', 'storeHome'].includes(locationType)) {
    return 'home';
  }
  return 'inStore';
};

export function asyncWait(timeToWait: MS): Promise<void> {
  return new Promise((resolve): void => {
    setTimeout((): void => {
      resolve();
    }, timeToWait);
  });
}

export const isConsolidatedCalendarEnabled = (
  currentService: string,
  appConfig: typeof CSS_CONFIG,
): boolean => {
  const { consolidatedCalendar } = appConfig.SERVICES;
  return (
    consolidatedCalendar.enabled &&
    !consolidatedCalendar.excludedServices.includes(currentService)
  );
};

export function removeTimePartFromDate(date: string): string {
  const [datePart] = date.split('T');
  return datePart;
}

export const isServiceLocationStore = (location: ServiceLocation): boolean => {
  return location === 'inStore';
};

export const isServiceProvidedRemote = (location: ServiceLocation): boolean => {
  return location === 'remote' || location === 'storeRemote';
};

export const isServiceProvidedAtHome = (location: ServiceLocation): boolean => {
  return location === 'home' || location === 'storeHome';
};

export const isTimeInBrowserSettings = (
  location: ServiceLocation,
  appConfig: typeof CSS_CONFIG,
): boolean => {
  return (
    appConfig.MULTIPLETIMEZONES &&
    (location === 'home' || location === 'remote' || location === 'storeHome')
  );
};

export const isStoreRemoteInConsolidatedCalendar = (
  location: ServiceLocation,
  currentService: string,
  appConfig: typeof CSS_CONFIG,
): boolean => {
  return (
    isConsolidatedCalendarEnabled(currentService, appConfig) &&
    location === 'storeRemote'
  );
};

export const isDistanceServiceProvidedByStore = (
  location: ServiceLocation,
): boolean => {
  return location === 'storeRemote' || location === 'storeHome';
};

export const getServiceLocationKey = (location: ServiceLocation): string => {
  if (isServiceProvidedAtHome(location)) {
    return 'home';
  }
  return location;
};

export const isStoreService = (location: ServiceLocation): boolean => {
  switch (location) {
    case 'inStore':
    case 'storeRemote':
    case 'storeHome':
      return true;
    case 'home':
    default:
      return false;
  }
};

export const isOutOfStoreService = (location: ServiceLocation): boolean => {
  return location === 'home' || location === 'remote';
};

export const startOfWeekDayToNumber = (day: string): 0 | 1 => {
  if (day.toUpperCase() === 'SUNDAY') {
    return 0;
  }
  return 1;
};

export const getReplacementServiceStoreName = (
  location: ServiceLocation,
  storeName: string,
  t: (x: string, y?: object | undefined) => string,
): string => {
  return isOutOfStoreService(location)
    ? t(`components.servicePicker.${getServiceLocationKey(location)}`)
    : storeName;
};

export const getReplacementServiceTitle = (
  location: ServiceLocation,
  replacementServiceTitle: string,
  t: (x: string, y?: object | undefined) => string,
): string => {
  return `${replacementServiceTitle} - ${t(`components.servicePicker.${getServiceLocationKey(location)}`)}`;
};

export const getDefaultReplacementStoreId = (
  stores: AvailableStore[],
  serviceId: string,
  serviceLocation: ServiceLocation,
  appConfig: typeof CSS_CONFIG,
): string => {
  const isConsolidatedCalendarUsed = isStoreRemoteInConsolidatedCalendar(
    serviceLocation,
    serviceId,
    appConfig,
  );

  if (isConsolidatedCalendarUsed) {
    const { defaultStorePostalCode } = appConfig.SERVICES.consolidatedCalendar;
    const defaultStore = stores.find(
      (store) => store.zipCode === defaultStorePostalCode,
    );
    return defaultStore ? defaultStore.value : CONSOLIDATED_STORE_ID;
  }

  return stores[0]?.value;
};

export const getReplacementServiceStoreId = (
  originalStoreId: string,
  replacementServiceId: string,
  replacementLocation: ServiceLocation,
  replacementStores: AvailableStore[],
  appConfig: typeof CSS_CONFIG,
): string | undefined => {
  if (!isStoreService(replacementLocation)) {
    return NO_STORE_ID;
  }

  const isOriginalStoreIdRemote = originalStoreId === NO_STORE_ID;
  if (isOriginalStoreIdRemote) {
    return getDefaultReplacementStoreId(
      replacementStores,
      replacementServiceId,
      replacementLocation,
      appConfig,
    );
  }

  const isOriginalStoreIdConsolidated =
    originalStoreId === CONSOLIDATED_STORE_ID;
  const shouldReplacementStoreUseConsolidatedId =
    isStoreRemoteInConsolidatedCalendar(
      replacementLocation,
      replacementServiceId,
      appConfig,
    );
  if (
    isOriginalStoreIdConsolidated &&
    shouldReplacementStoreUseConsolidatedId
  ) {
    return CONSOLIDATED_STORE_ID;
  }

  return replacementStores.find((store) => store.value === originalStoreId)
    ?.value;
};

export const getFormattedTimeSlots = (timeSlots: Slots[]) => {
  return timeSlots.map((slot: Slots) => ({
    date:
      typeof slot.date === 'string'
        ? parseISO(slot.date).toISOString()
        : slot.date,
    timeSlots: slot.timeSlots,
  }));
};

export const getSortedTimeSlots = (timeSlots: Slots[]) => {
  return timeSlots.toSorted((a: Slots, b: Slots) => {
    const aDate = new Date(a.date);
    const bDate = new Date(b.date);
    return aDate.getTime() - bDate.getTime();
  });
};
